import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import useSWR from 'swr';

import type { ByoLlmConfiguration } from '../../../../services/llm-gateway/LlmGateway.types';
import { getAvailableModelsForByoLlmConfiguration } from '../../../../services/llm-gateway/LlmGatewayService';
import { AutocompleteInputAdornment } from './AutocompleteInputAdornment';

interface AvailableModelsAutocompleteProps {
    organizationId: string;
    tenantId: string;
    byoLlmConfiguration: Partial<ByoLlmConfiguration>;
    label: string;
    initialValue: string;
    required?: boolean;
    disabled?: boolean;
    onChange?: (selectedModel?: string) => void;
}

export const AvailableModelsAutocomplete: React.FC<AvailableModelsAutocompleteProps> = ({
    organizationId, tenantId, byoLlmConfiguration, label, initialValue, required, disabled, onChange,
}: AvailableModelsAutocompleteProps) => {
    const [ value, setValue ] = useState<string>(initialValue);
    const [ selected, setSelected ] = useState<string | null>(null);
    const [ isOpen, setIsOpen ] = useState(false);

    useEffect(() => {
        setValue(initialValue);
        setSelected(initialValue);
    }, [ initialValue ]);

    useEffect(() => {
        if (byoLlmConfiguration) {
            byoLlmConfiguration.organizationId = organizationId;
            byoLlmConfiguration.tenantId = tenantId;
        }
    }, [ byoLlmConfiguration, organizationId, tenantId ]);

    const {
        data: models, isLoading: loading,
    } = useSWR(
        byoLlmConfiguration?.connectionId
            ? [ organizationId, tenantId, byoLlmConfiguration.connectionId ]
            : undefined,
        () => getAvailableModelsForByoLlmConfiguration(organizationId, tenantId, byoLlmConfiguration),
    );

    const handleChange = useCallback((_event: React.ChangeEvent<{}>, newValue: string | null) => {
        setSelected(newValue);
        if (onChange) {
            onChange(newValue ?? undefined);
        }
    }, [ onChange ]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    return (
        <Autocomplete
            style={{ width: '350px' }}
            options={models ?? []}
            freeSolo
            autoComplete
            inputValue={value}
            value={selected}
            data-cy="available-models-autocomplete"
            loading={loading}
            open={isOpen}
            disabled={disabled}
            onChange={handleChange}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            autoSelect
            onInputChange={(event, newValue) => {
                if (!event) {
                    return;
                }
                setValue(newValue);
            }}
            renderInput={(params) => (
                <TextField
                    required={required}
                    label={label}
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <AutocompleteInputAdornment
                                onClick={toggleOpen}
                                disabled={disabled}
                                isAutocompleteOpen={isOpen} />
                        ),
                    }}
                    fullWidth
                />
            )}
            renderOption={(optProps, option) => (
                <UiStack
                    p={SpacingToken.S as any}
                    {...optProps as any}>
                    <UiText>
                        {option}
                    </UiText>
                </UiStack>
            )}
        />
    );
};
